import { Plus } from 'lucide-react';
import { t } from '../../lib/i18n';
import { Button, CheckboxField, SelectField, TextField } from '../../components/ui';
import { useAllGroups } from './api';
import { NoticeTextEditor } from './NoticeTextEditor';
import type { PreviewOption } from './FieldPreview';

type Config = Record<string, unknown>;
type Prop = { type?: string; title?: string; default?: unknown };

const CUSTOM = new Set(['options', 'archivedOptions', 'message', 'requiredAnswer', 'groupIds']);

function newOptionId(): string {
  let id = '';
  while (id.length < 8) id += Math.floor(Math.random() * 36).toString(36);
  return id;
}

function propLabel(key: string, prop: Prop): string {
  const k = `fnUi.config.${key}`;
  const translated = t(k);
  return translated !== k ? translated : (prop.title ?? key);
}

/** Starting config for a type: the defaults its JSON Schema declares. */
export function defaultConfig(schema: Record<string, unknown>): Config {
  const props = (schema['properties'] ?? {}) as Record<string, Prop>;
  const out: Config = {};
  for (const [k, p] of Object.entries(props)) {
    if (p.default !== undefined) out[k] = p.default;
  }
  // The registry's schema defaults the choices to an empty list; start with one blank row to type into.
  if ('options' in props && (!Array.isArray(out['options']) || out['options'].length === 0)) out['options'] = [{ id: newOptionId(), label: '' }];
  if ('archivedOptions' in props && !Array.isArray(out['archivedOptions'])) out['archivedOptions'] = [];
  return out;
}

/** Drops blank choices before saving; returns the clean config. */
export function cleanConfig(config: Config): Config {
  if (!Array.isArray(config['options'])) return config;
  const options = (config['options'] as PreviewOption[]).map((o) => ({ ...o, label: o.label.trim() })).filter((o) => o.label);
  return { ...config, options };
}

function OptionsEditor({ config, onChange, disabled }: { config: Config; onChange: (c: Config) => void; disabled: boolean }) {
  const options = (Array.isArray(config['options']) ? config['options'] : []) as PreviewOption[];
  const archived = (Array.isArray(config['archivedOptions']) ? config['archivedOptions'] : []) as PreviewOption[];
  const set = (o: PreviewOption[], a: PreviewOption[]) => onChange({ ...config, options: o, archivedOptions: a });
  return (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-sm font-medium text-slate-800">{t('fnUi.config.options')}</legend>
      {options.map((o, i) => {
        const name = t('fnUi.config.optionLabel', { n: i + 1 });
        return (
          <div key={o.id} className="flex items-end gap-2">
            <TextField
              className="flex-1"
              label={<span className="sr-only">{name}</span>}
              value={o.label}
              disabled={disabled}
              maxLength={100}
              onChange={(e) => set(options.map((x) => (x.id === o.id ? { ...x, label: e.target.value } : x)), archived)}
            />
            <Button
              disabled={disabled}
              aria-label={t('fnUi.config.archiveOption', { name: o.label || name })}
              onClick={() => set(options.filter((x) => x.id !== o.id), o.label ? [...archived, o] : archived)}
            >
              {t('fnUi.dialog.archive')}
            </Button>
          </div>
        );
      })}
      <Button disabled={disabled || options.length >= 100} onClick={() => set([...options, { id: newOptionId(), label: '' }], archived)}>
        <Plus aria-hidden="true" className="h-4 w-4" />
        {t('fnUi.config.addOption')}
      </Button>
      {archived.length ? (
        <div className="pt-2">
          <p className="text-sm font-medium text-slate-800">{t('fnUi.config.archivedOptions')}</p>
          <p className="mb-1 text-xs text-slate-600">{t('fnUi.config.archivedHint')}</p>
          <ul className="space-y-1">
            {archived.map((o) => (
              <li key={o.id} className="flex items-center justify-between gap-2 text-sm text-slate-700">
                {o.label}
                <Button
                  disabled={disabled}
                  aria-label={t('fnUi.config.restoreOption', { name: o.label })}
                  onClick={() => set([...options, o], archived.filter((x) => x.id !== o.id))}
                >
                  {t('fnUi.config.restoreOption', { name: o.label })}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </fieldset>
  );
}

/** The form for a field type's settings, generated from the type's JSON Schema with custom editors for choices, messages and groups. */
export function ConfigForm({
  schema,
  config,
  onChange,
  disabled = false,
}: {
  schema: Record<string, unknown>;
  config: Config;
  onChange: (c: Config) => void;
  disabled?: boolean;
}) {
  const props = (schema['properties'] ?? {}) as Record<string, Prop>;
  const groups = useAllGroups();
  const setKey = (key: string, value: unknown) => {
    const next = { ...config };
    if (value === undefined || value === '') delete next[key];
    else next[key] = value;
    onChange(next);
  };
  const keys = Object.keys(props);
  return (
    <div className="space-y-4">
      {keys.includes('options') ? <OptionsEditor config={config} onChange={onChange} disabled={disabled} /> : null}
      {keys
        .filter((k) => !CUSTOM.has(k))
        .map((k) => {
          const p = props[k]!;
          const label = propLabel(k, p);
          if (p.type === 'boolean') {
            return <CheckboxField key={k} label={label} checked={Boolean(config[k])} disabled={disabled} onChange={(e) => setKey(k, e.target.checked)} />;
          }
          if (p.type === 'integer' || p.type === 'number') {
            const v = config[k];
            return (
              <TextField
                key={k}
                type="number"
                label={label}
                value={typeof v === 'number' ? String(v) : ''}
                disabled={disabled}
                onChange={(e) => setKey(k, e.target.value === '' ? undefined : Number(e.target.value))}
              />
            );
          }
          return <TextField key={k} label={label} value={typeof config[k] === 'string' ? (config[k] as string) : ''} disabled={disabled} onChange={(e) => setKey(k, e.target.value)} />;
        })}
      {keys.includes('requiredAnswer') ? (
        <SelectField
          label={t('fnUi.config.requiredAnswer')}
          disabled={disabled}
          value={config['requiredAnswer'] === true ? 'yes' : config['requiredAnswer'] === false ? 'no' : 'any'}
          onChange={(e) => onChange({ ...config, requiredAnswer: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null })}
        >
          <option value="any">{t('fnUi.config.requiredAnswer.any')}</option>
          <option value="yes">{t('fnUi.config.requiredAnswer.yes')}</option>
          <option value="no">{t('fnUi.config.requiredAnswer.no')}</option>
        </SelectField>
      ) : null}
      {keys.includes('message') ? (
        <NoticeTextEditor
          label={t('fnUi.config.message')}
          rows={5}
          disabled={disabled}
          value={typeof config['message'] === 'string' ? config['message'] : ''}
          onChange={(v) => setKey('message', v)}
        />
      ) : null}
      {keys.includes('groupIds') ? (
        <fieldset>
          <legend className="text-sm font-medium text-slate-800">{t('fnUi.config.groupIds')}</legend>
          <p className="mb-2 text-xs text-slate-600">{t('fnUi.config.groupIdsHint')}</p>
          <div className="space-y-2">
            {(groups.data ?? [])
              .filter((g) => g.kind === 'repeat')
              .map((g) => {
                const ids = (Array.isArray(config['groupIds']) ? config['groupIds'] : []) as string[];
                return (
                  <CheckboxField
                    key={g.id}
                    label={g.name}
                    disabled={disabled}
                    checked={ids.includes(g.id)}
                    onChange={(e) => onChange({ ...config, groupIds: e.target.checked ? [...ids, g.id] : ids.filter((x) => x !== g.id) })}
                  />
                );
              })}
          </div>
        </fieldset>
      ) : null}
    </div>
  );
}
